import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Clock, User, ArrowRight, BookOpen, Target, Megaphone, Settings, Sparkles, Cpu } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { B2C_ENABLED } from "../../config/features";
import InterestModal from "./InterestModal";

const translations = {
  overline: { es: "Catálogo · Avalado por PCMA", en: "Catalog · Endorsed by PCMA" },
  title: { es: "Elige tu especialización", en: "Choose Your Specialization" },
  subtitle: {
    es: B2C_ENABLED
      ? "6 cursos especializados para dominar cada área de la industria de reuniones. Cada curso desde"
      : "6 cursos especializados avalados por PCMA para desarrollar competencias clave, alineados con los estándares internacionales de la industria de reuniones.",
    en: B2C_ENABLED
      ? "6 specialized courses to master every area of the meetings industry. Each course from"
      : "6 specialized courses endorsed by PCMA to develop key competencies, aligned with the international standards of the meetings industry.",
  },
  categories: {
    es: ["Todos", "Fundamentos", "Atracción de eventos", "Marketing", "Operación", "Experiencias", "Innovación"],
    en: ["All", "Fundamentals", "Event Attraction", "Marketing", "Operations", "Experiences", "Innovation"],
  },
  modulesLabel: { es: "Módulos del curso", en: "Course modules" },
  fromMonth: { es: "desde $17/mes", en: "from $17/mo" },
  enroll: { es: "Inscribirme", en: "Enroll Now" },
  moreInfo: { es: "Quiero más información", en: "Request more information" },
  footer: {
    es: B2C_ENABLED
      ? "Cada curso se compra de forma individual en Hotmart • Certificado incluido"
      : "Cursos próximamente disponibles • Déjanos tus datos y te avisaremos",
    en: B2C_ENABLED
      ? "Each course is purchased individually on Hotmart • Certificate included"
      : "Courses coming soon • Leave your details and we'll let you know",
  },
  courses: {
    es: [
      {
        id: 1,
        title: "Qué es la Industria de Reuniones",
        instructor: "Eduardo Yarto",
        duration: "5 horas",
        icon: BookOpen,
        category: "Fundamentos",
        description: "Domina los conceptos esenciales del sector MICE y comprende cómo funciona el negocio de reuniones a nivel global.",
        topics: ["Introducción a la industria de reuniones", "Ecosistema del sector", "Actores de la industria", "Cómo funciona el negocio", "Tendencias globales"],
        color: "#2ED3C6"
      },
      {
        id: 2,
        title: "Definir el Proceso de Postulaciones (Bidding Process)",
        instructor: "Eduardo Yarto",
        duration: "5 horas",
        icon: Target,
        category: "Atracción de eventos",
        description: "Aprende a competir por eventos internacionales y construir candidaturas ganadoras para tu destino.",
        topics: ["Qué es el bidding process", "Cómo compiten los destinos", "Identificación de oportunidades", "Construcción de candidaturas", "Casos de éxito"],
        color: "#F59E0B"
      },
      {
        id: 3,
        title: "Marketing para Destinos de Reuniones",
        instructor: "Eduardo Yarto",
        duration: "5 horas",
        icon: Megaphone,
        category: "Marketing",
        description: "Desarrolla estrategias de marketing efectivas para posicionar destinos en el mercado de reuniones.",
        topics: ["Marketing de destinos de reuniones", "Segmentación", "Estrategias de promoción", "Marketing digital", "Marca destino"],
        color: "#2ED3C6"
      },
      {
        id: 4,
        title: "Excelencia en Operación y Estándares para Eventos",
        instructor: "Eduardo Yarto",
        duration: "5 horas",
        icon: Settings,
        category: "Operación",
        description: "Domina la logística, estándares internacionales y gestión operativa de eventos profesionales.",
        topics: ["Fundamentos de operación", "Logística y proveedores", "Experiencia del participante", "Estándares internacionales", "Gestión de crisis"],
        color: "#F59E0B"
      },
      {
        id: 5,
        title: "Productos Turísticos (Experiencias) para Eventos Especiales",
        instructor: "Tony Yarto",
        duration: "5 horas",
        icon: Sparkles,
        category: "Experiencias",
        description: "Diseña experiencias memorables que diferencien tus eventos y destinos de la competencia.",
        topics: ["Experiencia como diferenciador", "Diseño de experiencias", "Storytelling", "Experiencias turísticas", "Innovación y casos inspiradores"],
        color: "#2ED3C6"
      },
      {
        id: 6,
        title: "Inteligencia Artificial aplicada a la Industria de Reuniones",
        instructor: "Paola López",
        duration: "5 horas",
        icon: Cpu,
        category: "Innovación",
        description: "Descubre cómo aplicar IA para optimizar eventos, marketing y operaciones en la industria.",
        topics: ["Qué es la IA", "IA para eventos", "IA para marketing", "Herramientas prácticas", "Futuro de los eventos inteligentes"],
        color: "#F59E0B"
      }
    ],
    en: [
      {
        id: 1,
        title: "What is the Meetings Industry",
        instructor: "Eduardo Yarto",
        duration: "5 hours",
        icon: BookOpen,
        category: "Fundamentals",
        description: "Master the essential concepts of the MICE sector and understand how the meetings business works globally.",
        topics: ["Introduction to the meetings industry", "Sector ecosystem", "Industry stakeholders", "How the business works", "Global trends"],
        color: "#2ED3C6"
      },
      {
        id: 2,
        title: "Defining the Bidding Process",
        instructor: "Eduardo Yarto",
        duration: "5 hours",
        icon: Target,
        category: "Event Attraction",
        description: "Learn how to compete for international events and build winning bids for your destination.",
        topics: ["What is the bidding process", "How destinations compete", "Identifying opportunities", "Building candidacies", "Success stories"],
        color: "#F59E0B"
      },
      {
        id: 3,
        title: "Marketing for Meeting Destinations",
        instructor: "Eduardo Yarto",
        duration: "5 hours",
        icon: Megaphone,
        category: "Marketing",
        description: "Develop effective marketing strategies to position destinations in the meetings market.",
        topics: ["Meeting destination marketing", "Segmentation", "Promotion strategies", "Digital marketing", "Destination branding"],
        color: "#2ED3C6"
      },
      {
        id: 4,
        title: "Operational Excellence & Standards for Events",
        instructor: "Eduardo Yarto",
        duration: "5 hours",
        icon: Settings,
        category: "Operations",
        description: "Master logistics, international standards, and operational management of professional events.",
        topics: ["Operations fundamentals", "Logistics and suppliers", "Participant experience", "International standards", "Crisis management"],
        color: "#F59E0B"
      },
      {
        id: 5,
        title: "Tourism Products (Experiences) for Special Events",
        instructor: "Tony Yarto",
        duration: "5 hours",
        icon: Sparkles,
        category: "Experiences",
        description: "Design memorable experiences that differentiate your events and destinations from the competition.",
        topics: ["Experience as differentiator", "Experience design", "Storytelling", "Tourism experiences", "Innovation and inspiring cases"],
        color: "#2ED3C6"
      },
      {
        id: 6,
        title: "Artificial Intelligence Applied to the Meetings Industry",
        instructor: "Paola López",
        duration: "5 hours",
        icon: Cpu,
        category: "Innovation",
        description: "Discover how to apply AI to optimize events, marketing, and operations in the industry.",
        topics: ["What is AI", "AI for events", "AI for marketing", "Practical tools", "Future of smart events"],
        color: "#F59E0B"
      }
    ]
  }
};

// Direct Hotmart checkout URLs by course ID (shared ES/EN)
const HOTMART_URLS = {
  1: "https://pay.hotmart.com/M105591196O", // La industria de reuniones
  2: "https://pay.hotmart.com/S105595084L", // Bidding Process
  3: "https://pay.hotmart.com/G105594920F", // Marketing de un destino de reuniones
  4: "https://pay.hotmart.com/W105594964F", // Excelencia operativa y estándares
  5: "https://pay.hotmart.com/F105591357K", // Productos turísticos para eventos especiales
  6: "https://pay.hotmart.com/D105591394U", // Inteligencia artificial
};

const CourseCatalogSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { language, t } = useLanguage();
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [interestCourse, setInterestCourse] = useState(null);

  const categories = t(translations.categories);
  const courses = t(translations.courses);
  const activeCategory = categories[activeCategoryIndex];
  
  const filteredCourses = activeCategoryIndex === 0 
    ? courses 
    : courses.filter(course => course.category === activeCategory);

  const icons = [BookOpen, Target, Megaphone, Settings, Sparkles, Cpu];

  return (
    <section
      id="cursos"
      data-testid="course-catalog-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#0B1F3B] relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#2ED3C6] to-transparent opacity-50" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#2ED3C6]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#F59E0B]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 text-[#2ED3C6] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2 data-testid="course-catalog-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight mb-4">
            {t(translations.title)}
          </h2>
          <p className="font-body text-white/60 text-lg max-w-2xl mx-auto">
            {t(translations.subtitle)}{B2C_ENABLED && (<> <span className="text-[#F59E0B] font-semibold">$197 USD</span>.</>)}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category, index) => (
            <button
              key={category}
              onClick={() => setActiveCategoryIndex(index)}
              className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeCategoryIndex === index
                  ? "bg-[#2ED3C6] text-white"
                  : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredCourses.map((course, index) => {
            const Icon = icons[course.id - 1];
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                data-testid={`course-card-${course.id}`}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 hover:border-[#2ED3C6]/30 transition-all duration-300 flex flex-col"
              >
                <div className="p-6 pb-4 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${course.color}20` }}>
                      <Icon className="w-6 h-6" style={{ color: course.color }} />
                    </div>
                    <span className="text-xs font-body font-medium px-3 py-1 rounded-full" style={{ backgroundColor: `${course.color}20`, color: course.color }}>
                      {course.category}
                    </span>
                  </div>
                  <h3 className="font-display text-xl text-white font-medium mb-2 group-hover:text-[#2ED3C6] transition-colors leading-tight">
                    {course.title}
                  </h3>
                  <p className="font-body text-white/60 text-sm mb-4 leading-relaxed">{course.description}</p>
                  <div className="flex items-center gap-4 text-sm text-white/50 font-body mb-4">
                    <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{course.instructor}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{course.duration}</span>
                  </div>
                  <div className="space-y-2 mb-6 pt-4 border-t border-white/5">
                    <div className="text-[11px] uppercase tracking-wider font-body font-semibold text-white/40 mb-3">
                      {t(translations.modulesLabel)}
                    </div>
                    {course.topics.map((topic, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-white/70 font-body">
                        <div className="w-1.5 h-1.5 rounded-full mt-[7px] shrink-0" style={{ backgroundColor: course.color }} />
                        <span>{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="px-6 pb-6 mt-auto">
                  {B2C_ENABLED && (
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-2xl font-display font-bold text-white">$197</span>
                        <span className="text-white/50 text-sm font-body ml-1">USD</span>
                      </div>
                      <span className="text-xs text-white/40 font-body">{t(translations.fromMonth)}</span>
                    </div>
                  )}
                  {B2C_ENABLED ? (
                    <a
                      href={HOTMART_URLS[course.id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`course-cta-${course.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold py-3 px-4 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F59E0B]/20 group/btn"
                    >
                      {t(translations.enroll)}
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setInterestCourse({ id: course.id, name: course.title })}
                      data-testid={`course-cta-${course.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold py-3 px-4 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F59E0B]/20 group/btn"
                    >
                      {t(translations.moreInfo)}
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-white/50 font-body text-sm">{t(translations.footer)}</p>
        </motion.div>
      </div>

      <InterestModal
        open={!!interestCourse}
        onClose={() => setInterestCourse(null)}
        course={interestCourse}
      />
    </section>
  );
};

export default CourseCatalogSection;
